import RetourVente from './RetourVente';
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import TicketVente from './TicketVente';
import { authService } from '../services/auth.service';
import './Factures.css';

interface Vente {
  id: number;
  numero: string;
  clientNom: string;
  typeClient: string;
  modePaiement: string;
  totalHT: number;
  totalTVA: number;
  totalTTC: number;
  remiseGlobale: number;
  tvaActive: boolean;
  montantRecu: number;
  monnaie: number;
  statut: string;
  notes: string;
  boutiqueNom: string;
  createdAt: string;
  lignes?: any[];
}

const fmt = (n: number) => Math.round(Number(n)).toLocaleString('fr-FR');

const modeLabel = (mode: string) => {
  const labels: any = {
    especes: '💵 Espèces',
    orange_money: '🟠 Orange Money',
    moov_money: '🟡 Moov Money',
    wave: '🔵 Wave',
    credit: '💳 Crédit',
    virement: '🏦 Virement',
  };
  return labels[mode] || mode;
};

const statutBadge = (statut: string) => {
  if (statut === 'payee') return <span className="statut-badge payee">✓ Payée</span>;
  if (statut === 'credit') return <span className="statut-badge credit">💳 Crédit</span>;
  if (statut === 'annulee') return <span className="statut-badge annulee">✗ Annulée</span>;
  return <span className="statut-badge">{statut}</span>;
};

export default function Factures() {
  // Annulation et retour de vente : réservés à l'admin et au gérant (imposé
  // aussi côté serveur). Le vendeur consulte et réimprime les tickets.
  const peutAnnuler = ['admin', 'gerant'].includes(authService.getUser()?.role || '');
  const [ventes, setVentes] = useState<Vente[]>([]);
  const [chargement, setChargement] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [showTicket, setShowTicket] = useState(false);
  const [venteTicket, setVenteTicket] = useState<any>(null);
  const [showAnnulModal, setShowAnnulModal] = useState(false);
  const [venteAnnuler, setVenteAnnuler] = useState<Vente | null>(null);
  const [annulChargement, setAnnulChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [showRetour, setShowRetour] = useState(false);
const [venteRetour, setVenteRetour] = useState<any>(null);
  const [stats, setStats] = useState({
    total: 0, totalTTC: 0, credit: 0, annulees: 0,
  });

  useEffect(() => {
    chargerVentes();
  }, []);

  const chargerVentes = async () => {
    try {
      const res = await api.get('/ventes');
      setVentes(res.data);
      calculerStats(res.data);
    } catch (e) {
      setErreur('Impossible de charger les factures');
    } finally {
      setChargement(false);
    }
  };

  const calculerStats = (data: Vente[]) => {
    setStats({
      total: data.length,
      totalTTC: data.filter(v => v.statut !== 'annulee').reduce((s, v) => s + Number(v.totalTTC), 0),
      credit: data.filter(v => v.statut === 'credit').length,
      annulees: data.filter(v => v.statut === 'annulee').length,
    });
  };

  const ventesFiltrees = ventes.filter(v => {
    const matchStatut = filtreStatut === 'tous' || v.statut === filtreStatut;
    const matchRecherche = recherche === '' ||
      v.numero?.toLowerCase().includes(recherche.toLowerCase()) ||
      v.clientNom?.toLowerCase().includes(recherche.toLowerCase());
    return matchStatut && matchRecherche;
  });

  const voirTicket = async (vente: Vente) => {
    try {
      const res = await api.get(`/ventes/${vente.id}`);
      setVenteTicket(res.data);
      setShowTicket(true);
    } catch (e) {
      setErreur('Erreur lors du chargement de la vente');
    }
  };

  const ouvrirAnnulation = (v: Vente) => {
    setVenteAnnuler(v);
    setShowAnnulModal(true);
  };

  const confirmerAnnulation = async () => {
    if (!venteAnnuler) return;
    setAnnulChargement(true);
    setErreur('');
    try {
      await api.put(`/ventes/${venteAnnuler.id}/annuler`);
      setSucces(`Vente ${venteAnnuler.numero} annulée — stock remis à jour`);
      setShowAnnulModal(false);
      setVenteAnnuler(null);
      chargerVentes();
      setTimeout(() => setSucces(''), 4000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || "Erreur lors de l'annulation");
    } finally {
      setAnnulChargement(false);
    }
  };
  const ouvrirRetour = async (v: Vente) => {
  try {
    const res = await api.get(`/ventes/${v.id}`);
    setVenteRetour(res.data);
    setShowRetour(true);
  } catch (e) {
    setErreur('Erreur lors du chargement de la vente');
  }
};

  if (chargement) return <div className="page-chargement">Chargement des factures...</div>;

  return (
    <div className="factures-page">
      <div className="page-header">
        <div>
          <h1>Factures & Historique</h1>
          <p>Retrouvez toutes vos ventes et leurs détails</p>
        </div>
        <button className="btn-secondary" onClick={chargerVentes}>🔄 Actualiser</button>
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      {/* STATS */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-val">{stats.total}</span>
          <span className="stat-lbl">Total ventes</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: '#3B6D11', fontSize: 16 }}>
            {fmt(stats.totalTTC)} F
          </span>
          <span className="stat-lbl">CA total</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: '#854F0B' }}>{stats.credit}</span>
          <span className="stat-lbl">En crédit</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: '#A32D2D' }}>{stats.annulees}</span>
          <span className="stat-lbl">Annulées</span>
        </div>
      </div>

      {/* FILTRES */}
      <div className="search-bar">
        <input
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          placeholder="🔍 Rechercher par numéro ou client..."
        />
        <div className="filtre-type">
          {[
            { val: 'tous', label: 'Toutes' },
            { val: 'payee', label: '✓ Payées' },
            { val: 'credit', label: '💳 Crédit' },
            { val: 'annulee', label: '✗ Annulées' },
          ].map(f => (
            <button key={f.val}
              className={filtreStatut === f.val ? 'active' : ''}
              onClick={() => setFiltreStatut(f.val)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* TABLEAU */}
      <div className="factures-table">
        <table>
          <thead>
            <tr>
              <th>N° Facture</th>
              <th>Date</th>
              <th>Client</th>
              <th>Mode paiement</th>
              <th>Total TTC</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ventesFiltrees.map(v => (
              <tr key={v.id} className={v.statut === 'annulee' ? 'row-annulee' : ''}>
                <td className="fact-num">{v.numero}</td>
                <td className="fact-date">
                  <div>{new Date(v.createdAt).toLocaleDateString('fr-FR')}</div>
                  <div className="fact-heure">
                    {new Date(v.createdAt).toLocaleTimeString('fr-FR', {
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </div>
                </td>
                <td>
                  <div className="fact-client">{v.clientNom || 'Client de passage'}</div>
                  {v.typeClient && v.typeClient !== 'ordinaire' && (
                    <div className="fact-type">{v.typeClient}</div>
                  )}
                </td>
                <td>{modeLabel(v.modePaiement)}</td>
                <td className="fact-montant">{fmt(v.totalTTC)} FCFA</td>
                <td>{statutBadge(v.statut)}</td>
                <td>
                  <div className="td-actions">
                    <button
                      className="btn-ticket"
                      onClick={() => voirTicket(v)}
                      title="Voir le ticket">
                      🧾 Ticket
                    </button>
                    {peutAnnuler && v.statut !== 'annulee' && (
                      <button
                        className="btn-annuler-vente"
                        onClick={() => ouvrirAnnulation(v)}
                        title="Annuler la vente">
                        ✕ Annuler
                      </button>
                    )}
                    {peutAnnuler && v.statut !== 'annulee' && (
  <button
    className="btn-retour-vente"
    onClick={() => ouvrirRetour(v)}
    title="Retour produits">
    ↩️ Retour
  </button>
)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {ventesFiltrees.length === 0 && (
          <div className="table-empty">
            {recherche || filtreStatut !== 'tous'
              ? 'Aucune facture ne correspond aux critères'
              : 'Aucune vente enregistrée'}
          </div>
        )}
      </div>

      {/* TICKET */}
      {showTicket && venteTicket && (
        <TicketVente
          vente={venteTicket}
          onFermer={() => setShowTicket(false)}
        />
      )}

      {/* MODAL ANNULATION */}
      {showAnnulModal && venteAnnuler && (
        <div className="modal-overlay" onClick={() => setShowAnnulModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header danger">
              <h2>⚠️ Annuler la vente</h2>
              <button className="modal-close" onClick={() => setShowAnnulModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="annul-info">
                <div className="annul-row">
                  <span>Facture</span>
                  <span className="annul-val">{venteAnnuler.numero}</span>
                </div>
                <div className="annul-row">
                  <span>Client</span>
                  <span className="annul-val">
                    {venteAnnuler.clientNom || 'Client de passage'}
                  </span>
                </div>
                <div className="annul-row">
                  <span>Montant</span>
                  <span className="annul-val">{fmt(venteAnnuler.totalTTC)} FCFA</span>
                </div>
                <div className="annul-row">
                  <span>Date</span>
                  <span className="annul-val">
                    {new Date(venteAnnuler.createdAt).toLocaleString('fr-FR')}
                  </span>
                </div>
              </div>

              <div className="annul-consequences">
                <div className="ac-titre">Conséquences de l'annulation :</div>
                <div className="ac-item">📦 Le stock de chaque produit sera remis à jour</div>
                {venteAnnuler.statut === 'credit' && (
                  <div className="ac-item">💳 Le solde crédit du client sera ajusté</div>
                )}
                <div className="ac-item">📄 La facture sera marquée comme annulée</div>
                <div className="ac-item ac-warning">⚠️ Cette action est irréversible</div>
              </div>
            </div>
            {showRetour && venteRetour && (
  <RetourVente
    vente={venteRetour}
    onFermer={() => setShowRetour(false)}
    onRetourEffectue={() => {
      chargerVentes();
      setSucces('Retour enregistré — stock mis à jour');
      setTimeout(() => setSucces(''), 4000);
    }}
  />
)}
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAnnulModal(false)}>
                Fermer
              </button>
              <button
                className="btn-danger"
                onClick={confirmerAnnulation}
                disabled={annulChargement}>
                {annulChargement ? '⏳ Annulation...' : "✕ Confirmer l'annulation"}
              </button>
            </div>
          </div>
        </div>
              )}
    </div>
  );
}